import React from 'react';
import { Routes as RouterRoutes, Route, Navigate, matchRoutes } from 'react-router-dom';
import pagesRoutes, { Sitemap } from 'pages/routes';
// import blocksRoutes from 'blocks/routes';
// import demosRoutes from 'demos/routes';
import Auth0Return from './components/Auth0Return';
// import ProtectedRoute from './components/ProtectedRoute';
import nbaRoutes from './pages/NBA/nbaRoutes';
import wnbaRoutes from './pages/WNBA/wnbaRoutes';
import mensCBBRoutes from './pages/MensCBB/mensCBBRoutes';
import nflRoutes from './pages/NFL/nflRoutes';
import mlbRoutes from './pages/MLB/mlbRoutes';
import nhlRoutes from './pages/NHL/nhlRoutes';
import ncaafRoutes from './pages/NCAAF/ncaafRoutes';
import learnRoutes from 'pages/Learn/learnRoutes';
import quizRoutes from 'pages/Quiz/quizRoutes';
import toolsRoutes from './pages/Tools/toolsRoutes';
import blogRoutes from './pages/Blog/blogRoutes';
import { lazyPage } from './utils/lazyPage';

// Every page is its own chunk, so a landing downloads the app shell plus the one
// page it asked for. src/index.js loads that page before the first render
// (preloadRoute below); see utils/lazyPage for why that matters on prerendered
// pages.
const Account = lazyPage(() => import('pages/Account'));
const Terms = lazyPage(() => import('pages/Terms/Terms'));
const Privacy = lazyPage(() => import('pages/Privacy/Privacy'));
const ResponsibleGambling = lazyPage(() => import('pages/ResponsibleGambling/ResponsibleGambling'));
const Disclaimer = lazyPage(() => import('pages/Disclaimer'));
const CookiePolicy = lazyPage(() => import('pages/CookiePolicy'));
const Faq = lazyPage(() => import('pages/Faq/Faq'));
const About = lazyPage(() => import('pages/About'));
const Contact = lazyPage(() => import('pages/Contact'));
const NotFound = lazyPage(() => import('./pages/NotFound/NotFound'));
const Dashboard = lazyPage(() => import('pages/Dashboard'));
const Results = lazyPage(() => import('./pages/Results'));
const NFLHomePage = lazyPage(() => import('./pages/NFL/NFLHomePage'));
const NBAHomePage = lazyPage(() => import('./pages/NBA/NBAHomePage'));
const WNBAHomePage = lazyPage(() => import('./pages/WNBA/WNBAHomePage'));
const MLBHomePage = lazyPage(() => import('./pages/MLB/MLBHomePage'));
const NHLHomePage = lazyPage(() => import('./pages/NHL/NHLHomePage'));
const MensCBBHomePage = lazyPage(() => import('./pages/MensCBB/MensCBBHomePage'));
const NCAAFHomePage = lazyPage(() => import('./pages/NCAAF/NCAAFHomePage'));

const getBlogPriority = (path) => {
  if (path === '/learn') {
    return '0.9';  // Main blog index
  }
  if (path.includes('/learn/quick-start')) {
    return '0.8';  // Category pages
  }
  // Individual posts - could vary by age/importance
  return '0.7';
};

export const getSitemapRoutes = () => {
  return pagesRoutes.map(route => {
    // Base configuration
    let priority = '0.8';
    let changefreq = 'daily';  // Since these are betting pages, daily updates make sense

    // Customize based on route type
    if (route.path === '/') {
      priority = '1.0';  // Homepage gets highest priority
    } else if (route.path === '/sign-up') {
      priority = '0.9';
      changefreq = 'weekly';  // Sign-up page changes less frequently
    } else if (route.path.includes('/props')) {
      priority = '0.7';  // Props pages slightly lower priority
    }

    // Sport-specific priorities (you might want to adjust these based on season)
    const sportPriorities = {
      'nfl': '0.9',
      'ncaaf': '0.85',
      'nhl': '0.8',
      'mlb': '0.8',
      'mens-cbb': '0.85'
    };

    // Set sport-specific priority if applicable
    for (const [sport, sportPriority] of Object.entries(sportPriorities)) {
      if (route.path.includes(sport)) {
        priority = sportPriority;
        break;
      }
    }

    if (route.path.includes('/learn')) {
      priority = getBlogPriority(route.path);
      changefreq = route.path === '/learn' ? 'daily' : 'weekly';
    }

    return {
      path: route.path,
      priority,
      changefreq
    };
  });
};

// Sport tables skip their hub path: the hubs are registered directly above them
// so they bypass the season check.
const sportRoutes = (routes, hub) => routes
  .filter(route => route.path !== hub)
  .map(route => ({ path: route.path, element: route.renderer() }));

const fromTable = (routes) => routes.map(item => ({ path: item.path, element: item.renderer() }));

// #322: Learn pages merged into one definitive page per question. Flask 301s
// these on a fresh load (LEGACY_PATH_REDIRECTS in app/url_canonical.py); these
// entries cover client-side navigation from links that still point at the old
// URL, which never reaches the server and would otherwise render NotFound.
const MERGED_LEARN_REDIRECTS = {
  '/learn/guides/what-percentage-bettors-profitable': '/learn/guides/can-you-make-money-sports-betting',
  '/learn/guides/how-sharp-bettors-make-money': '/learn/guides/can-you-make-money-sports-betting',
  '/learn/guides/how-sportsbooks-make-money': '/learn/quick-start/what-is-vig-juice',
  '/learn/guides/live-betting-vs-pregame': '/learn/guides/when-to-bet-early-vs-late',
  '/learn/guides/why-betting-favorites-doesnt-work': '/learn/guides/why-do-i-keep-losing',
  '/learn/quick-start/covering-the-spread': '/learn/quick-start/point-spread-bets',
  '/learn/quick-start/player-props': '/learn/guides/props-vs-game-bets',
  '/learn/quick-start/round-robin-bets': '/learn/guides/why-parlays-always-lose',
  '/learn/quick-start/what-does-minus-110-mean': '/learn/quick-start/how-betting-odds-work',
  '/learn/strategies/sgp-correlation': '/learn/quick-start/same-game-parlay',
  '/learn/guides/walk-forward-validation': '/learn/strategies/models-find-hidden-value',
  '/learn/guides/how-we-optimize-our-model': '/learn/strategies/models-find-hidden-value',
};

const routeTable = [
  // Auth0 callback route must be before other routes
  { path: '/auth0-return', element: <Auth0Return /> },
  ...fromTable(pagesRoutes),
  { path: '/sitemap', element: <Sitemap /> },
  { path: '/edgeboard', element: <Dashboard /> },
  { path: '/account', element: <Account /> },

  // Direct routes to sport home pages that bypass season check
  { path: '/nfl', element: <NFLHomePage /> },
  { path: '/nba', element: <NBAHomePage /> },
  { path: '/wnba', element: <WNBAHomePage /> },
  { path: '/mlb', element: <MLBHomePage /> },
  { path: '/mlb/props', element: <Navigate to="/mlb/player-props" replace /> },
  { path: '/mlb/props/*', element: <Navigate to="/mlb/player-props" replace /> },
  { path: '/nhl', element: <NHLHomePage /> },
  { path: '/mens-cbb', element: <MensCBBHomePage /> },
  { path: '/ncaaf', element: <NCAAFHomePage /> },

  // Sports routes - always registered regardless of season status
  ...sportRoutes(nbaRoutes, '/nba'),
  ...sportRoutes(wnbaRoutes, '/wnba'),
  ...sportRoutes(mensCBBRoutes, '/mens-cbb'),
  ...sportRoutes(nflRoutes, '/nfl'),
  ...sportRoutes(mlbRoutes, '/mlb'),
  ...sportRoutes(nhlRoutes, '/nhl'),
  ...sportRoutes(ncaafRoutes, '/ncaaf'),

  ...Object.entries(MERGED_LEARN_REDIRECTS).map(([from, to]) => ({ path: from, element: <Navigate to={to} replace /> })),
  ...fromTable(learnRoutes),
  ...fromTable(quizRoutes),
  ...fromTable(toolsRoutes),
  ...fromTable(blogRoutes),

  { path: '/terms', element: <Terms /> },
  { path: '/privacy', element: <Privacy /> },
  { path: '/responsible-gambling', element: <ResponsibleGambling /> },
  { path: '/disclaimer', element: <Disclaimer /> },
  { path: '/cookies', element: <CookiePolicy /> },
  { path: '/faq', element: <Faq /> },
  { path: '/results', element: <Results /> },
  { path: '/about', element: <About /> },
  { path: '/contact', element: <Contact /> },
  { path: '*', element: <NotFound /> },
];

/**
 * Load the page component `pathname` will render, before React renders it.
 * Matched the way <Routes> matches, so the two cannot disagree. Resolves (never
 * rejects) once the chunk is in; a route with nothing to load resolves at once.
 */
export const preloadRoute = (pathname) => {
  const matches = matchRoutes(routeTable, pathname);
  const element = matches && matches[matches.length - 1].route.element;
  const preload = element && element.type && element.type.preload;
  return typeof preload === 'function' ? preload().catch(() => {}) : Promise.resolve();
};

const Routes = () => (
  <RouterRoutes>
    {routeTable.map((route, i) => (
      <Route key={i} path={route.path} element={route.element} />
    ))}
  </RouterRoutes>
);

export default Routes;
